
















import { Vue, Prop, Watch } from 'vue-property-decorator';
import { i18n, isQueryLanguageSet } from './config/translation/translation.config';
import { store } from './store/store';
import Ctc from './Ctc.vue';
import { initAssetServer } from './__webpack_public_path__';
import { initializeStore, Subscription } from '@seamless/store';
import { connectionName as wkoConnectionName, WellKnownObjects } from '@owc/connection-wko-manager';
import Component from 'vue-class-component';
import { OwcComponentRegistryObjects } from './seamlessStore.interface';
import { QueryParams } from './appConfig.interface';
import { ChargingOption, ApiInformation, Vehicle } from './store/modules/charging-data.interface';
import { ComponentConfig } from './store/modules/config';
import { getConfigByLocale } from './config/staticData/staticData.config';
import { NAMESPACE as wccConnectionName, VehicleDataSeamlessStoreState } from './WCC-Seamless';
import { SubBrand, Theme } from './store/modules/themes';

initAssetServer();
const seamlessStore = initializeStore();

@Component({
  store,
  i18n,
  name: 'eqweb-ctc-app',
  components: { Ctc },
})
export default class App extends Vue {
  @Prop()
  public readonly theme!: string;

  @Prop()
  public readonly subBrand: string | undefined;

  @Prop()
  public readonly asSidebar: boolean | undefined;

  @Prop()
  public readonly componentId!: string;

  private themeState: string | undefined;
  private subBrandState: string | undefined;
  private asSidebarState: boolean | undefined;

  private stage!: string;

  private locale!: string;

  private preSelectedModelSeriesDecision: {
    wko: string | null;
    query: string | null;
    mmv_vs: string | null;
  } = {
    wko: null,
    query: null,
    mmv_vs: null,
  };

  private preSelectedVehicleIds: string[] = [];
  private preSelectedVehicle: Vehicle | null = null;
  private chargingOptions: ChargingOption[] = [];
  private componentConfig!: ComponentConfig;
  private apiInformation!: ApiInformation;
  private wkoSubscription: Subscription | undefined = undefined;
  private wccSubscription: Subscription | undefined = undefined;
  private owcSubscription: Subscription | undefined = undefined;
  private mmvSubscription: Subscription | undefined = undefined;

  created(): void {
    this.asSidebarState = this.asSidebar;
    this.locale = i18n.locale;
    this.themeState = this.theme;
    this.subBrandState = this.subBrand;
    this.loadStaticDataByLocale(i18n.locale);

    const modelSeries = this.getQueryParam(QueryParams.MODELSERIES);
    if (modelSeries && modelSeries !== '') {
      this.preSelectedModelSeriesDecision.query = modelSeries;
    }
    const vehiclesIds = this.getQueryParamAsArray(QueryParams.VEHICLES);
    if (vehiclesIds) {
      this.preSelectedVehicleIds = vehiclesIds;
    }

    this.wkoSubscription = seamlessStore.subscribe(wkoConnectionName, this.seamlessStoreUpdate);
    this.wccSubscription = seamlessStore.subscribe(wccConnectionName, this.wccStoreInformation);
    this.owcSubscription = seamlessStore.subscribe('OWC_COMPONENT_REGISTRY', this.ctcStoreInformation);
    this.mmvSubscription = seamlessStore.subscribe('MMV-VS', this.mmvvsStoreInformation);
  }

  public destroyed(): void {
    this.wkoSubscription?.unsubscribe();
    this.wccSubscription?.unsubscribe();
    this.owcSubscription?.unsubscribe();
    this.mmvSubscription?.unsubscribe();
  }

  private get preSelectedModelSeries(): string {
    if (this.preSelectedModelSeriesDecision.query) {
      return this.preSelectedModelSeriesDecision.query;
    }
    if (this.preSelectedModelSeriesDecision.wko) {
      return this.preSelectedModelSeriesDecision.wko;
    }
    return this.preSelectedModelSeriesDecision.mmv_vs ?? '';
  }

  private getQueryParam(key: string): string | null {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    return urlParams.get(key);
  }

  private getQueryParamAsArray(key: string): string[] | null {
    const value = this.getQueryParam(key);
    if (!value) {
      return null;
    }
    return value
      .replace(/[[\]]/g, '')
      .split(',')
      .map((oneValue) => oneValue.trim());
  }

  private seamlessStoreUpdate(state: WellKnownObjects) {
    if (state.Environment?.stage) {
      this.stage = state.Environment.stage;
    }
    if (state.Locale && !isQueryLanguageSet()) {
      i18n.locale = `${state.Locale.language}_${state.Locale.country}`;
      this.loadStaticDataByLocale(i18n.locale);
      this.locale = i18n.locale;
    }
    if (state.Environment?.vehicleData?.modelSeries) {
      this.preSelectedModelSeriesDecision.wko = state.Environment.vehicleData.modelSeries;
    }
  }

  private loadStaticDataByLocale(locale: string) {
    const staticDataConfig = getConfigByLocale(locale);
    this.chargingOptions = staticDataConfig.t('chargingOptions') as unknown as ChargingOption[];
    this.componentConfig = staticDataConfig.t('componentConfig') as unknown as ComponentConfig;
    this.apiInformation = staticDataConfig.t('apiInformation') as unknown as ApiInformation;
  }

  private wccStoreInformation(state: VehicleDataSeamlessStoreState) {
    if (state.vehicleData) {
      this.preSelectedVehicle = state.vehicleData;
    }
  }

  private mmvvsStoreInformation(state: { considering?: { selectedVehicle?: { modelSeriesKey?: string } } }) {
    if (state.considering?.selectedVehicle?.modelSeriesKey) {
      this.preSelectedModelSeriesDecision.mmv_vs = state.considering.selectedVehicle.modelSeriesKey.slice(0, 4);
    }
  }

  private ctcStoreInformation(state: OwcComponentRegistryObjects) {
    const ctcRootComponent = state.components[this.componentId];
    if (ctcRootComponent?.payload?.vehicles && !this.getQueryParamAsArray(QueryParams.VEHICLES)) {
      const vehicles = ctcRootComponent.payload.vehicles.split(',').map((vehicle) => vehicle.trim());
      this.preSelectedVehicleIds = vehicles;
    }
    if (ctcRootComponent?.payload?.asSidebar !== undefined) {
      let asSidebar = ctcRootComponent.payload.asSidebar == true || ctcRootComponent.payload.asSidebar == 'true';

      if (this.asSidebarState === null || this.asSidebarState === undefined) {
        this.asSidebarState = asSidebar;
      }
    }
    if (ctcRootComponent?.payload?.dataSource) {
      let dataSource = ctcRootComponent.payload.dataSource;
      this.apiInformation.url = dataSource;
    }
  }

  @Watch('asSidebar')
  private onAsSidebarChanged(newAsSidebar: boolean): void {
    this.asSidebarState = newAsSidebar;
  }

  @Watch('theme')
  private onThemeChanged(newTheme: Theme): void {
    this.themeState = newTheme;
  }

  @Watch('subBrand')
  private onSubBrandChanged(newSubBrand: SubBrand): void {
    this.subBrandState = newSubBrand;
  }
}
